import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Box,
  Text,
  Center,
  HStack,
  Spacer,
  useToast,
  Spinner,
} from "@chakra-ui/react"
import NextLink from "next/link"
import { useTranslations } from "next-intl"
import { useEffect, useState } from "react"
import useKeywordListStore from "@/store/KeywordGroupListStore"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { MoreHorizontal, GripVertical } from "lucide-react"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

interface Keyword {
  id: string
  keyword: string
  order: number
  createdAt: string
  threadsUserId: string
}

interface KeywordGroup {
  id: string
  teamId: string
  logicOperator: "AND" | "OR"
  groupName: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  keywords: Keyword[]
  // Legacy compatibility
  keyword?: string
  nextUpdateTime?: string
}

interface SortableRowProps {
  group: KeywordGroup
  formatKeywordGroup: (group: KeywordGroup) => string | undefined
  onDelete: (id: string) => void
  t: ReturnType<typeof useTranslations>
  tCommon: ReturnType<typeof useTranslations>
}

function SortableRow({ group, formatKeywordGroup, onDelete, t, tCommon }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <TableRow ref={setNodeRef} style={style} className={isDragging ? "bg-muted" : ""}>
      <TableCell className="w-10">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      </TableCell>
      <TableCell>
        <div>
          <div className="font-medium">{formatKeywordGroup(group)}</div>
        </div>
      </TableCell>
      <TableCell className="text-right">
        <HStack>
          <Spacer />
          <NextLink href={`/threads/keyword/detail/${group.id}`} passHref>
            <Button>{t("table.details")}</Button>
          </NextLink>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    {t("table.delete")}
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{tCommon("title")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {tCommon("description")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{tCommon("cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={() => onDelete(group.id)}>
                      {tCommon("confirm")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        </HStack>
      </TableCell>
    </TableRow>
  )
}

function DashboardKeywordList() {
  const t = useTranslations("KeywordDashboard.KeywordList")
  const tCommon = useTranslations("Common.Alert")
  const { keywordGroups, isLoading, error, fetchKeywordGroups, deleteKeyword, reorderKeywordGroups } =
    useKeywordListStore()
  const toast = useToast()
  const [sortedGroups, setSortedGroups] = useState<KeywordGroup[]>([])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    fetchKeywordGroups()
  }, [fetchKeywordGroups])

  useEffect(() => {
    setSortedGroups(keywordGroups)
  }, [keywordGroups])

  const handleDelete = async (id: string) => {
    const result = await deleteKeyword(id)
    if (result.success) {
      toast({
        title: t("deleteSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      })
    } else {
      toast({
        title: t("deleteError"),
        description: result.error,
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const formatDateTime = (isoTimestamp: string) => {
    if (!isoTimestamp) return t("table.noUpdate")

    const date = new Date(isoTimestamp)

    // Format as yyyy/MM/DD HH:mm
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    const hours = String(date.getHours()).padStart(2, "0")
    const minutes = String(date.getMinutes()).padStart(2, "0")

    return `${year}/${month}/${day} ${hours}:${minutes}`
  }

  const formatKeywordGroup = (group: KeywordGroup) => {
    if (group.keywords && group.keywords.length > 1) {
      const keywordTexts = group.keywords.map((k) => k.keyword)
      return keywordTexts.join(` ${group.logicOperator} `)
    } else if (group.keywords && group.keywords.length === 1) {
      return group.keywords[0].keyword
    } else {
      return group.keyword
      // Legacy compatibility
      // return group.keyword || group.groupName
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = sortedGroups.findIndex((item) => item.id === active.id)
      const newIndex = sortedGroups.findIndex((item) => item.id === over.id)
      const newOrder = arrayMove(sortedGroups, oldIndex, newIndex)

      // Update UI immediately
      setSortedGroups(newOrder)

      // Call API to persist order
      const groupIds = newOrder.map((g) => g.id)
      const result = await reorderKeywordGroups(groupIds)

      if (!result.success) {
        // Revert on failure
        setSortedGroups(sortedGroups)
        toast({
          title: t("reorderError") || "Failed to reorder",
          status: "error",
          duration: 3000,
          isClosable: true,
        })
      }
    }
  }

  const tableComponent = (
    <div className="w-full">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>{t("table.header")}</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <SortableContext
              items={sortedGroups.map((g) => g.id)}
              strategy={verticalListSortingStrategy}
            >
              {sortedGroups.map((group: KeywordGroup) => (
                <SortableRow
                  key={group.id}
                  group={group}
                  formatKeywordGroup={formatKeywordGroup}
                  onDelete={handleDelete}
                  t={t}
                  tCommon={tCommon}
                />
              ))}
            </SortableContext>
          </TableBody>
        </Table>
      </DndContext>
    </div>
  )

  const emptyView = (
    <Box height="340px">
      <Center height="100%">
        <Text as="b" fontSize="xl">
          {t("emptyState")}
        </Text>
      </Center>
    </Box>
  )

  const loadingView = (
    <Box height="340px">
      <Center height="100%">
        <Spinner size="xl" />
      </Center>
    </Box>
  )

  const errorView = (
    <Box height="340px">
      <Center height="100%">
        <Text as="b" fontSize="xl" color="red.500">
          {error || "An error occurred while loading keyword groups"}
        </Text>
      </Center>
    </Box>
  )

  const renderContent = () => {
    if (isLoading) {
      return loadingView
    }
    if (error) {
      return errorView
    }
    if (keywordGroups.length === 0) {
      return emptyView
    }
    return tableComponent
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("title")}</CardTitle>
        <CardDescription>{t("description")}</CardDescription>
      </CardHeader>
      <CardContent>{renderContent()}</CardContent>
    </Card>
  )
}

export default DashboardKeywordList
